import React, { useState } from 'react'
import { X, Send, Mail, MessageSquare, Bug, Lightbulb, HelpCircle, Users, Building, Code, Upload, AlertTriangle } from 'lucide-react'
import { log } from '@/utils/logger'

interface ContactFormProps {
  isOpen: boolean
  onClose: () => void
}

const contactCategories = [
  { 
    value: 'general', 
    label: 'General Inquiries', 
    icon: HelpCircle, 
    color: 'text-blue-500',
    description: 'General questions and information'
  },
  { 
    value: 'feedback', 
    label: 'Feedback & Suggestions', 
    icon: MessageSquare, 
    color: 'text-green-500',
    description: 'Report bugs or suggest features'
  },
  { 
    value: 'validator', 
    label: 'Validator / Project Owner', 
    icon: Building, 
    color: 'text-purple-500',
    description: 'Update validator info or list projects'
  },
  { 
    value: 'partnerships', 
    label: 'Community & Partnerships', 
    icon: Users, 
    color: 'text-orange-500',
    description: 'Partnership and collaboration inquiries'
  },
  { 
    value: 'technical', 
    label: 'Technical / API Support', 
    icon: Code, 
    color: 'text-red-500',
    description: 'API questions and technical support'
  },
]

const generalSubjects = [
  'General Inquiry',
  'Support',
  'Feedback',
  'Business',
  'Other'
]

const feedbackTypes = [
  'Report a Bug',
  'Suggest a Feature',
  'UI/UX Feedback',
  'Data Issues',
  'Performance Issues'
]

const bugTypes = [
  'Explorer not loading / slow',
  'Wrong or missing data (blocks, transactions, validators)',
  'UI/UX issues',
  'Mobile responsiveness issues',
  'Other technical issue'
]

const featureTypes = [
  'New metrics or analytics',
  'API requests',
  'Dashboard improvements',
  'Mobile app features',
  'Other feature request'
]

const validatorTypes = [
  'Update validator logo/name/metadata',
  'List a project/token on explorer',
  'Report fraudulent validator/project',
  'Validator data corrections',
  'Other validator request'
]

const partnershipTypes = [
  'Integration partnerships',
  'Analytics tooling partnerships',
  'Media/press requests',
  'Open-source contribution interest',
  'Business development',
  'Other partnership inquiry'
]

const technicalTypes = [
  'API usage questions',
  'Data export requests',
  'Custom dashboard support',
  'Rate limiting issues',
  'Authentication problems',
  'Other technical question'
]

const priorityLevels = [
  { value: 'low', label: 'Low', color: 'text-green-600' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
  { value: 'high', label: 'High', color: 'text-red-600' },
]

export const ContactForm: React.FC<ContactFormProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'general',
    subject: '',
    subType: '',
    bugType: '',
    featureType: '',
    validatorName: '',
    priority: 'medium',
    message: '',
    newsletter: false,
    attachment: null as File | null
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const formDataToSend = new FormData()
      
      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'attachment' && value) {
          if (value instanceof File) {
            formDataToSend.append('attachment', value)
          }
        } else if (key !== 'attachment') {
          formDataToSend.append(key, String(value))
        }
      })

      // Add metadata
      formDataToSend.append('timestamp', new Date().toISOString())
      formDataToSend.append('userAgent', navigator.userAgent)
      formDataToSend.append('url', window.location.href)

      const response = await fetch('/api/contact', {
        method: 'POST',
        body: formDataToSend,
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          email: '',
          category: 'general',
          subject: '',
          subType: '',
          bugType: '',
          featureType: '',
          validatorName: '',
          priority: 'medium',
          message: '',
          newsletter: false,
          attachment: null
        })
        setTimeout(() => {
          onClose()
          setSubmitStatus('idle')
        }, 3000)
      } else {
        throw new Error('Failed to send message')
      }
    } catch (error) {
      log.error('Contact form error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setFormData(prev => ({ ...prev, attachment: file }))
  }

  const selectedCategory = contactCategories.find(cat => cat.value === formData.category)
  const getSubTypeOptions = () => {
    switch (formData.category) {
      case 'general': return generalSubjects
      case 'feedback': return feedbackTypes
      case 'validator': return validatorTypes
      case 'partnerships': return partnershipTypes
      case 'technical': return technicalTypes
      default: return []
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Contact FortiBlox Explorer</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Get help, report issues, or share feedback</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Contact Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              What can we help you with?
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {contactCategories.map((category) => {
                const IconComponent = category.icon
                return (
                  <label
                    key={category.value}
                    className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.category === category.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="category"
                      value={category.value}
                      checked={formData.category === category.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className="flex items-center space-x-3 mb-2">
                      <IconComponent className={`w-5 h-5 ${category.color}`} />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {category.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {category.description}
                    </p>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Name and Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Name / Alias <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-pill focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Your name or preferred alias"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-pill focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="your@email.com"
              />
            </div>
          </div>

          {/* Category-specific fields */}
          {formData.category && (
            <div className="space-y-4">
              {/* Subject/Type Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type of Request <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="subType"
                    value={formData.subType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-pill focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a type...</option>
                    {getSubTypeOptions().map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Priority Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Priority Level
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-pill focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    {priorityLevels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Additional Bug Type Selection */}
              {formData.subType === 'Report a Bug' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bug Type
                  </label>
                  <select
                    name="bugType"
                    value={formData.bugType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-pill focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select bug type...</option>
                    {bugTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Additional Feature Type Selection */}
              {formData.subType === 'Suggest a Feature' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Feature Type
                  </label>
                  <select
                    name="featureType"
                    value={formData.featureType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-pill focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select feature type...</option>
                    {featureTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Validator Name for Validator Category */}
              {formData.category === 'validator' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Validator Name & Identity
                  </label>
                  <input
                    type="text"
                    name="validatorName"
                    value={formData.validatorName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-pill focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Your validator name or public key"
                  />
                </div>
              )}
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-pill focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={`Brief description of your ${selectedCategory?.label.toLowerCase()}`}
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-pill focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-none"
              placeholder="Please provide as much detail as possible about your request or issue..."
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Attach Screenshot/Log <span className="text-gray-400">(optional)</span>
            </label>
            <div className="flex items-center space-x-3">
              <label className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-pill cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <Upload className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Choose file</span>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*,.txt,.log,.json"
                  className="sr-only"
                />
              </label>
              {formData.attachment && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.attachment.name}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Supported: Images, text files, logs (max 10MB)
            </p>
          </div>

          {/* Newsletter Checkbox */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="newsletter"
              name="newsletter"
              checked={formData.newsletter}
              onChange={handleInputChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="newsletter" className="text-sm text-gray-700 dark:text-gray-300">
              Add me to updates/newsletter
            </label>
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="p-4 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200 font-medium">
                ✓ Message sent successfully! We've sent you a confirmation email and will get back to you soon.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="p-4 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                ✗ Failed to send message. Please try again or email us directly at explorer@fortiblox.com
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-pill transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-pill transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send Message</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}